/* =============================================================
   StaffPerformancePage — /staff/performance
   Ажилтан бүрийн өдөр болон сарын гүйцэтгэлийн хяналт
   - Өдрийн зорилт: 10 хүн / ажилтан
   - Сарын зорилт: 200 хүн / ажилтан
   - Гараар зөвлөгөө нэмэх форм (биечлэн, утсаар, бусад)
   - Гүйцэтгэлийн хувь, анхааруулга
   ============================================================= */
import { useState, useMemo } from "react";
import {
  Trophy, Target, TrendingUp, Plus, X, Check,
  User, Phone, Globe, Calendar, ChevronDown,
  AlertTriangle, CheckCircle2, Clock, Flame,
  BarChart3, Trash2, RefreshCw, Star,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { STAFF_MEMBERS } from "@shared/staff";

/* ── Constants ───────────────────────────────────────────────── */
const DAILY_GOAL = 10;
const MONTHLY_GOAL = 200;
const COUNTRY_LABELS: Record<string, string> = {
  usa: "🇺🇸 АНУ", australia: "🇦🇺 Австрали", canada: "🇨🇦 Канад",
  ireland: "🇮🇪 Ирланд", uk: "🇬🇧 Их Британи", schengen: "🇪🇺 Шенген",
  czech: "🇨🇿 Чех", newzealand: "🇳🇿 Шинэ Зеланд", other: "🌍 Бусад",
};

const CONSULT_TYPE_LABELS: Record<string, string> = {
  in_person: "🏢 Биечлэн",
  phone: "📞 Утсаар",
  online: "💻 Онлайн",
  other: "📋 Бусад",
};

function formatDate(d: Date | string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleString("mn-MN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/* ── Progress Ring ───────────────────────────────────────────── */
function ProgressRing({ pct, size = 80, stroke = 7, color }: { pct: number; size?: number; stroke?: number; color: string }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
    </svg>
  );
}

/* ── Staff Card ──────────────────────────────────────────────── */
function StaffCard({
  daily,
  monthly,
  onAddClick,
}: {
  daily: { staffName: string; dailyCount: number; dailyGoal: number; dailyPct: number; goalReached: boolean; manualCount: number; crmCount: number };
  monthly: { staffName: string; monthlyCount: number; monthlyGoal: number; monthlyPct: number; goalReached: boolean } | undefined;
  onAddClick: (staffName: string) => void;
}) {
  const name = daily.staffName;
  const dailyColor = daily.goalReached ? "#34d399" : daily.dailyPct >= 50 ? "#fbbf24" : "#f87171";
  const monthlyColor = monthly?.goalReached ? "#34d399" : (monthly?.monthlyPct ?? 0) >= 50 ? "#fbbf24" : "#a78bfa";
  const isLow = daily.dailyPct < 30 && !daily.goalReached;

  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-4 transition-all"
      style={{
        background: daily.goalReached ? "rgba(52,211,153,0.06)" : isLow ? "rgba(248,113,113,0.06)" : "rgba(255,255,255,0.04)",
        border: daily.goalReached ? "1px solid rgba(52,211,153,0.30)" : isLow ? "1px solid rgba(248,113,113,0.25)" : "1px solid rgba(255,255,255,0.08)",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm"
            style={{ background: "rgba(124,58,237,0.20)", color: "#a78bfa", border: "1px solid rgba(124,58,237,0.30)" }}
          >
            {name[0]}
          </div>
          <div>
            <p className="text-white font-bold text-base">{name}</p>
            {daily.goalReached ? (
              <span className="flex items-center gap-1 text-xs text-emerald-400 font-semibold">
                <CheckCircle2 className="w-3 h-3" /> Өдрийн зорилт биелсэн!
              </span>
            ) : isLow ? (
              <span className="flex items-center gap-1 text-xs text-red-400 font-semibold">
                <AlertTriangle className="w-3 h-3" /> Анхааруулга: {DAILY_GOAL - daily.dailyCount} хүн дутуу
              </span>
            ) : (
              <span className="text-white/40 text-xs">{DAILY_GOAL - daily.dailyCount} хүн үлдсэн</span>
            )}
          </div>
        </div>
        <button
          onClick={() => onAddClick(name)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80"
          style={{ background: "rgba(124,58,237,0.20)", color: "#a78bfa", border: "1px solid rgba(124,58,237,0.30)" }}
        >
          <Plus className="w-3.5 h-3.5" />
          Нэмэх
        </button>
      </div>

      {/* Progress rings */}
      <div className="flex items-center gap-6">
        {/* Daily */}
        <div className="flex flex-col items-center gap-1">
          <div className="relative">
            <ProgressRing pct={daily.dailyPct} size={72} stroke={6} color={dailyColor} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-white font-black text-lg leading-none">{daily.dailyCount}</span>
              <span className="text-white/40 text-[9px]">/{DAILY_GOAL}</span>
            </div>
          </div>
          <span className="text-white/40 text-[11px] font-medium">Өнөөдөр</span>
          <span className="font-bold text-sm" style={{ color: dailyColor }}>{daily.dailyPct}%</span>
        </div>

        {/* Monthly */}
        <div className="flex flex-col items-center gap-1">
          <div className="relative">
            <ProgressRing pct={monthly?.monthlyPct ?? 0} size={72} stroke={6} color={monthlyColor} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-white font-black text-lg leading-none">{monthly?.monthlyCount ?? 0}</span>
              <span className="text-white/40 text-[9px]">/{MONTHLY_GOAL}</span>
            </div>
          </div>
          <span className="text-white/40 text-[11px] font-medium">Энэ сар</span>
          <span className="font-bold text-sm" style={{ color: monthlyColor }}>{monthly?.monthlyPct ?? 0}%</span>
        </div>

        {/* Breakdown */}
        <div className="flex-1 flex flex-col gap-2 ml-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/40">💻 CRM (онлайн)</span>
            <span className="text-white/70 font-semibold">{daily.crmCount}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/40">🏢 Гараар нэмсэн</span>
            <span className="text-white/70 font-semibold">{daily.manualCount}</span>
          </div>
          <div className="mt-1 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/60 font-semibold">Нийт өнөөдөр</span>
            <span className="text-white font-bold">{daily.dailyCount}</span>
          </div>
        </div>
      </div>

      {/* Daily progress bar */}
      <div>
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-white/35">Өдрийн явц</span>
          <span style={{ color: dailyColor }} className="font-bold">{daily.dailyPct}% ({daily.dailyCount}/{DAILY_GOAL})</span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${daily.dailyPct}%`, background: dailyColor }}
          />
        </div>
      </div>
    </div>
  );
}

/* ── Inline Add Consultation Form ───────────────────────────── */
function InlineAddForm({ onSuccess }: { onSuccess: () => void }) {
  const [staffName, setStaffName] = useState<typeof STAFF_MEMBERS[number]>(STAFF_MEMBERS[0]);
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [country, setCountry] = useState("");
  const [visaCategory, setVisaCategory] = useState("");
  const [consultationType, setConsultationType] = useState<"in_person" | "phone" | "online" | "other">("in_person");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const addMut = trpc.staffPerformance.addConsultation.useMutation({
    onSuccess: () => {
      onSuccess();
      setClientName(""); setClientPhone(""); setClientEmail(""); setCountry(""); setVisaCategory(""); setNotes(""); setError("");
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    },
    onError: (e) => setError(e.message),
  });

  const handleSubmit = () => {
    if (!staffName || !clientName) { setError("Ажилтны нэр болон үйлчлүүлэгчийн нэр заавал бөлгөнө үү"); return; }
    addMut.mutate({ staffName, clientName, clientPhone: clientPhone || undefined, clientEmail: clientEmail || undefined, country: country || undefined, visaCategory: visaCategory || undefined, consultationType, notes: notes || undefined });
  };

  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-4"
      style={{ background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.25)" }}
    >
      <div className="flex items-center gap-2">
        <Plus className="w-4 h-4 text-violet-400" />
        <h2 className="text-white font-bold text-base">Зөвлөгөө нэмэх</h2>
        <span className="text-white/30 text-xs ml-1">Биечлэн, утсаар болон бусад зөвлөгөөг бүртгэнэ</span>
      </div>

      {/* Row 1: Staff + Type */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1">Ажилтан *</label>
          <select
            value={staffName}
            onChange={e => setStaffName(e.target.value as typeof STAFF_MEMBERS[number])}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none appearance-none cursor-pointer"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          >
            {STAFF_MEMBERS.map(n => <option key={n} value={n} style={{ background: "#130D3A" }}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1">Төрөл</label>
          <select
            value={consultationType}
            onChange={e => setConsultationType(e.target.value as any)}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none appearance-none cursor-pointer"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          >
            {(["in_person", "phone", "online", "other"] as const).map(t => (
              <option key={t} value={t} style={{ background: "#130D3A" }}>{CONSULT_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1">Үйлчлүүлэгчийн нэр *</label>
          <input
            type="text"
            placeholder="Нэр оруулах..."
            value={clientName}
            onChange={e => setClientName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSubmit()}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          />
        </div>
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1">Утас</label>
          <input
            type="tel"
            placeholder="99xxxxxx"
            value={clientPhone}
            onChange={e => setClientPhone(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSubmit()}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          />
        </div>
      </div>

      {/* Row 2: Email + Country + Visa + Notes */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1">Имэйл</label>
          <input
            type="email"
            placeholder="example@gmail.com"
            value={clientEmail}
            onChange={e => setClientEmail(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          />
        </div>
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1">Улс</label>
          <select
            value={country}
            onChange={e => setCountry(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none appearance-none cursor-pointer"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", color: country ? "white" : "rgba(255,255,255,0.35)" }}
          >
            <option value="" style={{ background: "#130D3A" }}>Сонгох...</option>
            {Object.entries(COUNTRY_LABELS).map(([v, l]) => (
              <option key={v} value={v} style={{ background: "#130D3A" }}>{l}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1">Виз ангилал</label>
          <input
            type="text"
            placeholder="Аялал, ажил..."
            value={visaCategory}
            onChange={e => setVisaCategory(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          />
        </div>
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1">Тэмдэглэл</label>
          <input
            type="text"
            placeholder="Яриа, санал..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          />
        </div>
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}
      {success && <p className="text-emerald-400 text-xs font-semibold">✓ Зөвлөгөө амжилттай бүртгэгдлээ!</p>}

      <button
        onClick={handleSubmit}
        disabled={addMut.isPending}
        className="flex items-center justify-center gap-2 w-full sm:w-auto sm:self-end px-6 py-3 rounded-xl font-bold text-sm transition-all hover:opacity-80 disabled:opacity-50"
        style={{ background: "rgba(124,58,237,0.30)", color: "#c4b5fd", border: "1px solid rgba(124,58,237,0.50)" }}
      >
        {addMut.isPending ? (
          <div className="w-4 h-4 rounded-full border-2 border-violet-400/30 border-t-violet-400 animate-spin" />
        ) : (
          <Check className="w-4 h-4" />
        )}
        Зөвлөгөө бүртгэх
      </button>
    </div>
  );
}

/* ── Add Consultation Modal ──────────────────────────────────── */
function AddConsultationModal({
  initialStaff,
  onClose,
  onSuccess,
}: {
  initialStaff: typeof STAFF_MEMBERS[number];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [staffName, setStaffName] = useState(initialStaff);
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [country, setCountry] = useState("");
  const [visaCategory, setVisaCategory] = useState("");
  const [consultationType, setConsultationType] = useState<"in_person" | "phone" | "online" | "other">("in_person");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const addMut = trpc.staffPerformance.addConsultation.useMutation({
    onSuccess: () => { onSuccess(); onClose(); },
    onError: (e) => setError(e.message),
  });

  const handleSubmit = () => {
    if (!staffName || !clientName) { setError("Ажилтны нэр болон үйлчлүүлэгчийн нэр заавал бөглөнө үү"); return; }
    addMut.mutate({ staffName, clientName, clientPhone: clientPhone || undefined, clientEmail: clientEmail || undefined, country: country || undefined, visaCategory: visaCategory || undefined, consultationType, notes: notes || undefined });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.70)" }} onClick={onClose} />
      <div
        className="relative w-full max-w-md rounded-2xl p-6 flex flex-col gap-5"
        style={{ background: "#130D3A", border: "1px solid rgba(255,255,255,0.12)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg">Зөвлөгөө нэмэх</h2>
            <p className="text-white/40 text-xs mt-0.5">Биечлэн, утсаар болон бусад зөвлөгөөг бүртгэнэ</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-white/50">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Staff name */}
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1.5">
            <User className="w-3 h-3 inline mr-1" />Ажилтны нэр *
          </label>
          <select
            value={staffName}
            onChange={e => setStaffName(e.target.value as typeof STAFF_MEMBERS[number])}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none appearance-none cursor-pointer"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          >
            {STAFF_MEMBERS.map(n => <option key={n} value={n} style={{ background: "#130D3A" }}>{n}</option>)}
          </select>
        </div>

        {/* Consultation type */}
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1.5">Зөвлөгөөний төрөл</label>
          <div className="grid grid-cols-2 gap-2">
            {(["in_person", "phone", "online", "other"] as const).map(t => (
              <button
                key={t}
                onClick={() => setConsultationType(t)}
                className="px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left"
                style={{
                  background: consultationType === t ? "rgba(124,58,237,0.25)" : "rgba(255,255,255,0.05)",
                  border: `1px solid ${consultationType === t ? "rgba(124,58,237,0.50)" : "rgba(255,255,255,0.10)"}`,
                  color: consultationType === t ? "#c4b5fd" : "rgba(255,255,255,0.50)",
                }}
              >
                {CONSULT_TYPE_LABELS[t]}
              </button>
            ))}
          </div>
        </div>

        {/* Client name */}
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1.5">
            Үйлчлүүлэгчийн нэр *
          </label>
          <input
            type="text"
            placeholder="Нэр оруулах..."
            value={clientName}
            onChange={e => setClientName(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          />
        </div>

        {/* Phone (optional) */}
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1.5">
            <Phone className="w-3 h-3 inline mr-1" />Утас (заавал биш)
          </label>
          <input
            type="tel"
            placeholder="99xxxxxx"
            value={clientPhone}
            onChange={e => setClientPhone(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          />
        </div>

        {/* Email (optional — triggers satisfaction email) */}
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1.5">
            ✉️ Имэйл (байвал сэтгэл ханамжийн мэйл явуулна)
          </label>
          <input
            type="email"
            placeholder="example@gmail.com"
            value={clientEmail}
            onChange={e => setClientEmail(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          />
        </div>

        {/* Country + visa */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1.5">
              <Globe className="w-3 h-3 inline mr-1" />Улс
            </label>
            <select
              value={country}
              onChange={e => setCountry(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none appearance-none cursor-pointer"
              style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", color: country ? "white" : "rgba(255,255,255,0.35)" }}
            >
              <option value="" style={{ background: "#130D3A" }}>Сонгох...</option>
              {Object.entries(COUNTRY_LABELS).map(([v, l]) => (
                <option key={v} value={v} style={{ background: "#130D3A" }}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1.5">Виз ангилал</label>
            <input
              type="text"
              placeholder="Аялал, ажил..."
              value={visaCategory}
              onChange={e => setVisaCategory(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25"
              style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="text-white/40 text-xs font-semibold uppercase tracking-wide block mb-1.5">Тэмдэглэл</label>
          <textarea
            rows={2}
            placeholder="Яриа, санал, дараагийн алхам..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl text-white text-sm outline-none placeholder-white/25 resize-none"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}
          />
        </div>

        {error && <p className="text-red-400 text-xs">{error}</p>}

        <button
          onClick={handleSubmit}
          disabled={addMut.isPending}
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-bold text-sm transition-all hover:opacity-80 disabled:opacity-50"
          style={{ background: "rgba(124,58,237,0.30)", color: "#c4b5fd", border: "1px solid rgba(124,58,237,0.50)" }}
        >
          {addMut.isPending ? (
            <div className="w-4 h-4 rounded-full border-2 border-violet-400/30 border-t-violet-400 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          Зөвлөгөө бүртгэх
        </button>
      </div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────── */
export default function StaffPerformancePage() {
  const [addModalStaff, setAddModalStaff] = useState<string | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"daily" | "monthly">("daily");

  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Ulaanbaatar" });
  const now = new Date();

  const { data: dailyStats = [], refetch: refetchDaily } = trpc.staffPerformance.getDailyStats.useQuery({ date: today });
  const { data: monthlyStats = [], refetch: refetchMonthly } = trpc.staffPerformance.getMonthlyStats.useQuery({
    year: now.getFullYear(), month: now.getMonth() + 1,
  });
  const { data: ratingStats = [] } = trpc.staffPerformance.getRatingStats.useQuery();
  const { data: manualList = [], refetch: refetchManual } = trpc.staffPerformance.listManual.useQuery({
    staffName: selectedStaff ?? undefined, limit: 100,
  });
  const { data: crmContactedList = [], refetch: refetchCRM } = trpc.staffPerformance.listCRMContacted.useQuery({
    staffName: selectedStaff ?? undefined, limit: 100,
  });
  const deleteMut = trpc.staffPerformance.deleteConsultation.useMutation({
    onSuccess: () => { refetchManual(); refetchDaily(); refetchMonthly(); },
  });

  const refetchAll = () => { refetchDaily(); refetchMonthly(); refetchManual(); refetchCRM(); };

  // Summary stats
  const totalToday = useMemo(() => dailyStats.reduce((s, r) => s + r.dailyCount, 0), [dailyStats]);
  const goalReachedCount = useMemo(() => dailyStats.filter(r => r.goalReached).length, [dailyStats]);
  const teamDailyPct = useMemo(() => {
    const total = STAFF_MEMBERS.length * DAILY_GOAL;
    return Math.min(Math.round((totalToday / total) * 100), 100);
  }, [totalToday]);

  return (
    <div className="min-h-screen" style={{ background: "#0D0A2E" }}>
      {/* Top bar */}
      <div
        className="flex items-center gap-3 px-5 py-4 flex-wrap sticky top-0 z-10"
        style={{ background: "#0D0A2E", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
      >
        <BarChart3 className="w-5 h-5 text-violet-400" />
        <h1 className="text-white font-bold text-lg mr-auto">Ажилтны Гүйцэтгэл</h1>
        <button
          onClick={refetchAll}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/40"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 flex flex-col gap-6">
        {/* CRM link */}
        <div className="flex items-center gap-2">
          <a
            href="/staff/crm"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80"
            style={{ background: "rgba(96,165,250,0.15)", color: "#60a5fa", border: "1px solid rgba(96,165,250,0.30)" }}
          >
            CRM
          </a>
        </div>

        {/* Inline Add Consultation Form */}
        <InlineAddForm onSuccess={refetchAll} />

        {/* Team summary */}
        <div
          className="rounded-2xl p-5 grid grid-cols-2 sm:grid-cols-4 gap-4"
          style={{ background: "rgba(124,58,237,0.10)", border: "1px solid rgba(124,58,237,0.25)" }}
        >
          <div className="flex flex-col gap-1">
            <span className="text-white/40 text-xs uppercase tracking-wide">Өнөөдрийн нийт</span>
            <span className="text-white font-black text-3xl">{totalToday}</span>
            <span className="text-white/40 text-xs">/{STAFF_MEMBERS.length * DAILY_GOAL} зорилт</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-white/40 text-xs uppercase tracking-wide">Багийн явц</span>
            <span className="text-violet-300 font-black text-3xl">{teamDailyPct}%</span>
            <span className="text-white/40 text-xs">өдрийн зорилтоос</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-white/40 text-xs uppercase tracking-wide">Зорилт биелүүлсэн</span>
            <span className="text-emerald-400 font-black text-3xl">{goalReachedCount}</span>
            <span className="text-white/40 text-xs">/{STAFF_MEMBERS.length} ажилтан</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-white/40 text-xs uppercase tracking-wide">Өдрийн зорилт</span>
            <span className="text-white font-black text-3xl">{DAILY_GOAL}</span>
            <span className="text-white/40 text-xs">хүн/ажилтан</span>
          </div>
        </div>

        {/* Rating stats */}
        {ratingStats.length > 0 && (
          <div className="rounded-2xl p-5" style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.20)" }}>
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-4 h-4 text-yellow-400" fill="#fbbf24" />
              <span className="text-white/60 text-sm font-semibold uppercase tracking-wide">Сэтгэл ханамжийн үнэлгээ</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {ratingStats.map(r => (
                <div key={r.staffName} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: "rgba(124,58,237,0.20)", color: "#a78bfa" }}>{r.staffName[0]}</div>
                    <span className="text-white text-sm font-semibold">{r.staffName}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} className="w-3.5 h-3.5" fill={n <= Math.round(r.avgRating) ? "#fbbf24" : "none"} stroke={n <= Math.round(r.avgRating) ? "#fbbf24" : "#555"} strokeWidth={1.5} />
                    ))}
                    <span className="text-yellow-400 font-bold text-xs ml-1">{r.avgRating}</span>
                    <span className="text-white/30 text-xs">({r.ratingCount})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View toggle */}
        <div className="flex gap-2">
          {(["daily", "monthly"] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
              style={{
                background: viewMode === mode ? "rgba(124,58,237,0.25)" : "rgba(255,255,255,0.05)",
                color: viewMode === mode ? "#c4b5fd" : "rgba(255,255,255,0.45)",
                border: `1px solid ${viewMode === mode ? "rgba(124,58,237,0.40)" : "rgba(255,255,255,0.10)"}`,
              }}
            >
              {mode === "daily" ? "📅 Өдрийн явц" : "📊 Сарын явц"}
            </button>
          ))}
        </div>

        {viewMode === "daily" ? (
          /* ── Daily view ── */
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {STAFF_MEMBERS.map(name => {
              const daily = dailyStats.find(d => d.staffName === name) ?? {
                staffName: name, dailyCount: 0, dailyGoal: DAILY_GOAL, dailyPct: 0,
                goalReached: false, manualCount: 0, crmCount: 0, entries: [],
              };
              const monthly = monthlyStats.find(m => m.staffName === name);
              return (
                <StaffCard
                  key={name}
                  daily={daily}
                  monthly={monthly}
                  onAddClick={setAddModalStaff}
                />
              );
            })}
          </div>
        ) : (
          /* ── Monthly view ── */
          <div className="flex flex-col gap-3">
            <div
              className="rounded-xl p-4 flex items-center gap-3"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <Trophy className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="text-white font-semibold text-sm">Сарын зорилт: {MONTHLY_GOAL} хүн / ажилтан</p>
                <p className="text-white/40 text-xs">{now.toLocaleDateString("mn-MN", { year: "numeric", month: "long" })}</p>
              </div>
            </div>
            {STAFF_MEMBERS.map(name => {
              const m = monthlyStats.find(s => s.staffName === name) ?? {
                staffName: name, monthlyCount: 0, monthlyGoal: MONTHLY_GOAL, monthlyPct: 0, goalReached: false,
              };
              const color = m.goalReached ? "#34d399" : m.monthlyPct >= 50 ? "#fbbf24" : "#a78bfa";
              return (
                <div
                  key={name}
                  className="rounded-xl p-4"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                      style={{ background: "rgba(124,58,237,0.20)", color: "#a78bfa" }}
                    >
                      {name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-semibold text-sm">{name}</span>
                        <span className="font-bold text-sm" style={{ color }}>{m.monthlyPct}%</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-white/40 mt-0.5">
                        <span>{m.monthlyCount} / {MONTHLY_GOAL} хүн</span>
                        {m.goalReached && <span className="text-emerald-400 font-semibold">✓ Биелсэн</span>}
                      </div>
                    </div>
                  </div>
                  <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${m.monthlyPct}%`, background: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CRM contacted list */}
        {crmContactedList.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-white/60 font-semibold text-sm uppercase tracking-wide">CRM — Холбогдсон хүмүүсийн жагсаалт</h2>
              <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
              <span className="text-emerald-400 text-xs font-bold">{crmContactedList.length} хүн</span>
            </div>
            <div className="flex flex-col gap-2">
              {crmContactedList.map(item => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-3.5 rounded-xl"
                  style={{ background: "rgba(52,211,153,0.04)", border: "1px solid rgba(52,211,153,0.15)" }}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                    style={{ background: "rgba(52,211,153,0.15)", color: "#34d399" }}
                  >
                    {(item.assignedTo ?? "?")[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-semibold text-sm">{item.name}</span>
                      {item.country && <span className="text-white/40 text-xs">{COUNTRY_LABELS[item.country] ?? item.country}</span>}
                      {item.serviceName && <span className="text-emerald-400/70 text-xs">{item.serviceName}</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {item.assignedTo && <span className="text-white/35 text-xs">&#128100; {item.assignedTo}</span>}
                      {item.phone && (
                        <a href={`tel:${item.phone}`} className="text-blue-400 text-xs hover:text-blue-300">
                          &#128222; {item.phone}
                        </a>
                      )}
                      {item.lastContactedAt && (
                        <span className="text-white/25 text-xs">{new Date(item.lastContactedAt).toLocaleString("mn-MN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                      )}
                    </div>
                    {item.notes && <p className="text-white/40 text-xs mt-1 italic">{item.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Manual consultation log */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <h2 className="text-white/60 font-semibold text-sm uppercase tracking-wide">Гараар нэмсэн зөвлөгөөнүүд</h2>
            <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
            {/* Staff filter */}
            <select
              value={selectedStaff ?? ""}
              onChange={e => setSelectedStaff(e.target.value || null)}
              className="px-3 py-1.5 rounded-lg text-white text-xs outline-none appearance-none cursor-pointer"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: selectedStaff ? "white" : "rgba(255,255,255,0.40)" }}
            >
              <option value="" style={{ background: "#130D3A" }}>Бүх ажилтан</option>
              {STAFF_MEMBERS.map(n => <option key={n} value={n} style={{ background: "#130D3A" }}>{n}</option>)}
            </select>
          </div>

          {manualList.length === 0 ? (
            <div
              className="rounded-xl p-8 flex flex-col items-center gap-2"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
              <Plus className="w-8 h-8 text-white/20" />
              <p className="text-white/30 text-sm">Гараар нэмсэн зөвлөгөө байхгүй байна</p>
              <button
                onClick={() => setAddModalStaff(STAFF_MEMBERS[0])}
                className="mt-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80"
                style={{ background: "rgba(124,58,237,0.20)", color: "#c4b5fd", border: "1px solid rgba(124,58,237,0.30)" }}
              >
                Эхний зөвлөгөөг нэмэх
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {manualList.map(item => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-3.5 rounded-xl"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                    style={{ background: "rgba(124,58,237,0.20)", color: "#a78bfa" }}
                  >
                    {item.staffName[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-semibold text-sm">{item.clientName}</span>
                      <span className="text-white/40 text-xs">{CONSULT_TYPE_LABELS[item.consultationType]}</span>
                      {item.country && <span className="text-white/40 text-xs">{COUNTRY_LABELS[item.country] ?? item.country}</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-white/35 text-xs">👤 {item.staffName}</span>
                      {item.clientPhone && (
                        <a href={`tel:${item.clientPhone}`} className="text-blue-400 text-xs hover:text-blue-300">
                          📞 {item.clientPhone}
                        </a>
                      )}
                      <span className="text-white/25 text-xs">{formatDate(item.consultedAt)}</span>
                    </div>
                    {item.notes && <p className="text-white/40 text-xs mt-1 italic">{item.notes}</p>}
                  </div>
                  <button
                    onClick={() => deleteMut.mutate({ id: item.id })}
                    className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/25 hover:text-red-400 transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
